import React, { useState, useEffect } from 'react';
import api from '../api';

const STAGES = [
  { key: 'applied', label: 'Applied' },
  { key: 'screening', label: 'Screening' },
  { key: 'interview', label: 'Interview' },
  { key: 'offer', label: 'Offer' },
  { key: 'hired', label: 'Hired' },
  { key: 'rejected', label: 'Rejected' },
];

const blankCandidate = { name: '', email: '', phone: '', position: '', stage: 'applied', source: '', notes: '' };

const Recruitment = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [edit, setEdit] = useState(null);       // candidate being added/edited
  const [hire, setHire] = useState(null);        // candidate being hired
  const [hireForm, setHireForm] = useState({ job_title: '', department: '', start_date: '', pay_rate: '', weekly_hours: '41.5' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/recruitment/candidates');
      setCandidates(r.data);
    } catch (err) {
      setError('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const moveStage = async (c, stage) => {
    try { await api.put(`/api/recruitment/candidates/${c.id}`, { ...c, stage }); load(); }
    catch (err) { setError('Failed to update stage'); }
  };

  const save = async () => {
    if (!edit.name) return setError('Name required');
    try {
      if (edit.id) await api.put(`/api/recruitment/candidates/${edit.id}`, edit);
      else await api.post('/api/recruitment/candidates', edit);
      setSuccess('Saved');
      setEdit(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
  };

  const remove = async () => {
    try { await api.delete(`/api/recruitment/candidates/${edit.id}`); setEdit(null); load(); }
    catch (err) { setError('Failed to delete'); }
  };

  const doHire = async () => {
    try {
      const r = await api.post(`/api/recruitment/candidates/${hire.id}/hire`, hireForm);
      setSuccess(r.data.message || 'Hired');
      setHire(null);
      setHireForm({ job_title: '', department: '', start_date: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to hire');
    }
  };

  const byStage = (key) => candidates.filter(c => c.stage === key);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Candidates</h1>
          <p className="page-subtitle">Your recruitment pipeline, from application through to hire.</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEdit({ ...blankCandidate }); setError(''); }}>+ Add Candidate</button>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess('')}>{success}</div>}

      {loading ? (
        <div className="loader">Loading...</div>
      ) : (
        <div style={{ display: 'flex', gap: '14px', overflowX: 'auto', paddingBottom: '8px' }}>
          {STAGES.map(s => (
            <div key={s.key} style={{ minWidth: '230px', flex: '1 0 230px' }}>
              <div style={{ fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--grey-mid)', fontWeight: 600, padding: '0 4px 8px' }}>
                {s.label} <span style={{ color: 'var(--navy)' }}>({byStage(s.key).length})</span>
              </div>
              <div style={{ background: 'var(--grey-light)', borderRadius: '6px', padding: '8px', minHeight: '120px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {byStage(s.key).map(c => (
                  <div key={c.id} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '5px', padding: '12px', boxShadow: 'var(--shadow)' }}>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--navy)' }}>{c.name}</div>
                    {c.position && <div style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{c.position}</div>}
                    {c.email && <div style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '2px' }}>{c.email}</div>}
                    <select className="form-control" style={{ marginTop: '10px', padding: '5px 8px', fontSize: '12px' }} value={c.stage} onChange={e => moveStage(c, e.target.value)}>
                      {STAGES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                    </select>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                      <button className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={() => { setEdit(c); setError(''); }}>Edit</button>
                      {!c.hired_user_id && (
                        <button className="btn btn-success btn-sm" style={{ flex: 1 }} onClick={() => { setHire(c); setHireForm({ job_title: c.position || '', department: '', start_date: '', pay_rate: '', weekly_hours: '41.5' }); setError(''); }}>Hire</button>
                      )}
                    </div>
                    {c.hired_user_id && <div style={{ fontSize: '11px', color: 'var(--success)', marginTop: '6px', fontWeight: 600 }}>✓ Onboarding started</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / edit candidate */}
      {edit && (
        <div className="modal-overlay" onClick={() => setEdit(null)}>
          <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{edit.id ? 'Edit Candidate' : 'Add Candidate'}</h3>
              <button className="modal-close" onClick={() => setEdit(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Full Name</label>
                  <input className="form-control" value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" value={edit.email || ''} onChange={e => setEdit({ ...edit, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-control" value={edit.phone || ''} onChange={e => setEdit({ ...edit, phone: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Position</label>
                  <input className="form-control" value={edit.position || ''} onChange={e => setEdit({ ...edit, position: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Stage</label>
                  <select className="form-control" value={edit.stage} onChange={e => setEdit({ ...edit, stage: e.target.value })}>
                    {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Source</label>
                  <input className="form-control" value={edit.source || ''} onChange={e => setEdit({ ...edit, source: e.target.value })} placeholder="e.g. Indeed, referral, walk-in" />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" value={edit.notes || ''} onChange={e => setEdit({ ...edit, notes: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <div>{edit.id && <button className="btn btn-danger" onClick={remove}>Delete</button>}</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-outline" onClick={() => setEdit(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={save}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hire */}
      {hire && (
        <div className="modal-overlay" onClick={() => setHire(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Hire {hire.name}</h3>
              <button className="modal-close" onClick={() => setHire(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>
                This creates an onboarding login for <strong>{hire.email}</strong> and emails them their details.
              </p>
              <div className="form-group">
                <label className="form-label">Job Title</label>
                <input className="form-control" value={hireForm.job_title} onChange={e => setHireForm(f => ({ ...f, job_title: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <input className="form-control" value={hireForm.department} onChange={e => setHireForm(f => ({ ...f, department: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input type="date" className="form-control" value={hireForm.start_date} onChange={e => setHireForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group">
                  <label className="form-label">Pay Rate (£/hour)</label>
                  <input type="number" min="0" step="0.01" className="form-control" value={hireForm.pay_rate} onChange={e => setHireForm(f => ({ ...f, pay_rate: e.target.value }))} placeholder="e.g. 12.50" />
                </div>
                <div className="form-group">
                  <label className="form-label">Weekly Hours</label>
                  <input type="number" min="0" step="0.5" className="form-control" value={hireForm.weekly_hours} onChange={e => setHireForm(f => ({ ...f, weekly_hours: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setHire(null)}>Cancel</button>
              <button className="btn btn-success" onClick={doHire}>Hire &amp; start onboarding</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recruitment;
